import React from 'react';
import PropTypes from 'prop-types';

import './index.scss';

/**
 * MarkerIcon - A reusable map marker icon component
 * 
 * This component displays a customizable map marker icon that can be used
 * in map views and filter panels. The color is controlled via CSS custom properties.
 * 
 * @param {string} color - The marker color (hex format, e.g., '#5f8cff')
 * @param {string} size - The size variant: 'small', 'medium', or 'large'
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 * @param {string} ariaLabel - Accessibility label
 */
export default function MarkerIcon({
    color = '#5f8cff',
    size = 'medium',
    className = '',
    style = {},
    ariaLabel = 'Map marker',
}) {
    const combinedClassName = [
        'marker-icon',
        `marker-icon--${size}`,
        className,
    ].filter(Boolean).join(' ');

    // Calculate darker shade for gradient effect
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        } : null;
    };

    const rgb = hexToRgb(color) || { r: 95, g: 140, b: 255 };
    const darkerColor = `rgb(${Math.max(0, rgb.r - 30)}, ${Math.max(0, rgb.g - 30)}, ${Math.max(0, rgb.b - 30)})`;
    
    // Generate unique IDs for SVG gradients/filters to avoid conflicts
    const uniqueId = `marker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const safeId = uniqueId.replace(/[^a-zA-Z0-9-]/g, '-');

    return (
        <span
            className={combinedClassName}
            style={style}
            aria-label={ariaLabel}
            role="img"
        >
            <svg
                className="marker-icon__svg"
                viewBox="0 0 24 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <defs>
                    <filter id={`marker-shadow-${safeId}`} x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="2.5"/>
                        <feOffset dx="0" dy="3" result="offsetblur"/>
                        <feComponentTransfer>
                            <feFuncA type="linear" slope="0.35"/>
                        </feComponentTransfer>
                        <feMerge>
                            <feMergeNode/>
                            <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                    </filter>
                    <linearGradient id={`marker-gradient-${safeId}`} x1="12" y1="2" x2="12" y2="28" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor={color}/>
                        <stop offset="100%" stopColor={darkerColor}/>
                    </linearGradient>
                </defs>
                
                {/* Pin shadow */}
                <path
                    d="M12 2C7.58 2 4 5.58 4 10C4 16 12 28 12 28C12 28 20 16 20 10C20 5.58 16.42 2 12 2Z"
                    fill="rgba(0, 0, 0, 0.2)"
                    transform="translate(0, 2)"
                />
                
                {/* Pin body */}
                <path
                    d="M12 2C7.58 2 4 5.58 4 10C4 16 12 28 12 28C12 28 20 16 20 10C20 5.58 16.42 2 12 2Z"
                    fill={`url(#marker-gradient-${safeId})`}
                    stroke="rgba(255, 255, 255, 0.3)"
                    strokeWidth="1.5"
                    filter={`url(#marker-shadow-${safeId})`}
                />
                
                {/* Highlight circle */}
                <ellipse cx="12" cy="10" rx="4.5" ry="4.5" fill="rgba(255, 255, 255, 0.35)"/>
                <ellipse cx="12" cy="9.5" rx="3" ry="3" fill="rgba(255, 255, 255, 0.5)"/>
                
                {/* Center dot */}
                <circle cx="12" cy="10" r="1.8" fill="rgba(255, 255, 255, 0.85)"/>
            </svg>
        </span>
    );
}

MarkerIcon.propTypes = {
    color: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    className: PropTypes.string,
    style: PropTypes.object,
    ariaLabel: PropTypes.string,
};
